import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { connectivityMonitor } from '../../helpers/connectivityMonitor';
import { eventSubmissionQueue } from '../../helpers/eventSubmissionQueue';
import { errorWithUserMessage } from '../../helpers/NotificationHelper';
import { moveShootoutRow, shootoutRows } from '../../helpers/shootoutOrder';
import { ApiException, EventTypeEnum, EventVm, MatchDetailVm, MatchesClient, ReorderShootoutCommand } from '../../utils/api';
import './shootoutOrder.css';

const useCanEditShootout = (matchId: number): boolean => {
    const [online, setOnline] = useState(connectivityMonitor.getCanFlush());
    const [pending, setPending] = useState(true);
    useEffect(() => {
        let live = true;
        const refresh = async (): Promise<void> => {
            const rows = await eventSubmissionQueue.listByMatch(matchId);
            if (live) setPending(rows.length > 0);
        };
        const connection = connectivityMonitor.subscribeLink(() => setOnline(connectivityMonitor.getCanFlush()));
        const queue = eventSubmissionQueue.subscribe(() => { void refresh(); });
        void refresh();
        return () => { live = false; connection(); queue(); };
    }, [matchId]);
    return online && !pending;
};

const ShootoutOrderControls = ({ match, events, onApplied, refresh, children }: {
    match: MatchDetailVm; events: EventVm[];
    onApplied: (rows: EventVm[]) => void; refresh: () => Promise<void>;
    children: (controls: (row: EventVm) => React.ReactNode) => React.ReactNode;
}): React.ReactElement => {
    const canEdit = useCanEditShootout(match.id);
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const rows = shootoutRows(events);
    const move = async (id: number, direction: -1 | 1): Promise<void> => {
        if (inFlight.current || !canEdit) return;
        inFlight.current = true;
        setBusy(true);
        try {
            if (!connectivityMonitor.getCanFlush() || (await eventSubmissionQueue.listByMatch(match.id)).length) return;
            const result = await new MatchesClient().reorderShootout(match.id, new ReorderShootoutCommand({
                eventIds: moveShootoutRow(rows.map(row => row.id), id, direction), expectedRevision: match.revision ?? 0,
            }));
            onApplied(result);
            await refresh();
        } catch (error) {
            const apiError = error as ApiException;
            let message = 'The shootout could not be reordered. Try again.';
            if (apiError.status === 409) {
                message = 'The shootout changed. Refresh and try again.';
                if (apiError.response?.includes('That player is already the goalkeeper.')) message = 'That player is already the goalkeeper.';
            }
            errorWithUserMessage(message);
            await refresh();
        } finally { inFlight.current = false; setBusy(false); }
    };
    const controls = (row: EventVm): React.ReactNode => {
        if (row.typeId !== EventTypeEnum.Goalkeeper_Change || row.shootoutOrder == null) return null;
        const index = rows.findIndex(item => item.id === row.id);
        return <span className="c-event__shootout-controls" onClick={e => e.stopPropagation()}>
            {index < rows.length - 1 && <Button size="small" aria-label={`Move row ${row.shootoutOrder} up`} icon={<ArrowUpOutlined />}
                disabled={!canEdit || busy} onClick={() => void move(row.id, 1)} />}
            {index > 0 && <Button size="small" aria-label={`Move row ${row.shootoutOrder} down`} icon={<ArrowDownOutlined />}
                disabled={!canEdit || busy} onClick={() => void move(row.id, -1)} />}
        </span>;
    };
    return <>
        {!match.syncLocked && (match.hungaryGameId || match.eurocupMatchKey || match.championsLeagueMatchKey) && <Alert type="info" showIcon message="This match still syncs from an external source. Lock it before fixing the shootout order." />}
        {children(controls)}
    </>;
};
export default ShootoutOrderControls;
